import { useOutletContext } from 'react-router-dom';
import type { AppLayoutContext } from '~/types';
import { useMyFamilyBillingQuery } from './useMyFamilyBillingQuery';
import { ParentInvoicesList, type InvoiceData } from './ParentInvoicesList';
import { ParentPaymentMethodCard } from './ParentPaymentMethodCard';
import { ParentPaymentMethodsPanel } from './ParentPaymentMethodsPanel';
import { useViewAsFamily } from '~/hooks/useViewAsFamily';

export function MyInvoices() {
  const { school, roleContext } = useOutletContext<AppLayoutContext>();
  const selectedYear = roleContext?.selectedSchoolYear;
  const { familyId: viewAsFamilyId, readOnly } = useViewAsFamily(school?.slug);
  const { data, loading, error, refetch } = useMyFamilyBillingQuery(school?.id, selectedYear?.id, viewAsFamilyId ?? undefined);

  if (loading) {
    return <div className="p-6 text-muted-foreground">Loading your billing…</div>;
  }
  if (error) {
    return <div className="p-6 text-destructive">Something went wrong loading your billing.</div>;
  }

  const billing = data?.myFamilyBilling;
  if (!billing) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold">Billing &amp; Invoices</h1>
        <p className="mt-4 text-muted-foreground">No billing information for this school year yet.</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8">
      <div>
        <h1 className="text-2xl font-semibold">Billing &amp; Invoices</h1>
        {selectedYear ? <p className="text-muted-foreground">{selectedYear.name}</p> : null}
      </div>

      {billing.familyEnrollmentId ? (
        <ParentPaymentMethodCard
          paymentMethod={billing.paymentMethod}
          familyEnrollmentId={billing.familyEnrollmentId}
          readOnly={readOnly}
        />
      ) : null}

      {billing.familyEnrollmentId && !readOnly ? (
        <ParentPaymentMethodsPanel
          familyEnrollmentId={billing.familyEnrollmentId}
          onChanged={() => { void refetch(); }}
        />
      ) : null}

      <ParentInvoicesList
        invoices={(billing.invoices ?? []) as InvoiceData[]}
        familyEnrollmentId={readOnly ? null : billing.familyEnrollmentId}
        onPaymentStarted={() => { void refetch(); }}
      />
    </div>
  );
}
